import React, { useState, useMemo } from "react";
import { Modal } from "../../../components/Modal";
import { Select } from "../../../components/Select";
import { theme } from "../../../styles/theme";
import { Div } from "../../../styles/Common";
import { Alert, Col, Row } from "antd";
import { Button } from "../../../components/Button";
import { Link } from "../../../components/Link";
import addIcon from "../../../assets/icons/add.svg";
import deleteIcon from "../../../assets/icons/trash-salmon.svg";
import useAssignUser from "../../UsersByProject/hooks/useAssignUser";

export const AddUserToProjectModal = ({
  openCreate,
  handleClose,
  usersInfo,
  setUsersInfo,
  rolesOptions,
  activeUsersOptions,
  setUserRolesProject,
  userRolesProject,
  existingUsers = [],
  source,
  projectId,
  refetch,
}) => {
  const assignUser = useAssignUser();
  const [errorMessage, setErrorMessage] = useState("");
  const [forceUpdate, setForceUpdate] = useState(false);
  
  const getFilteredRolesForUser = (userId, currentIndex) => {
    if (!userId) return rolesOptions;
    
    const userAssignedRolesFromState = userRolesProject
      .filter(userRole => userRole.userId === userId)
      .map(userRole => userRole.roleId);
    
    const userAssignedRolesFromAPI = existingUsers
      .filter(userRole => userRole.userId === userId)
      .map(userRole => userRole.roleId);
    
    const userAssignedRoles = [...userAssignedRolesFromState, ...userAssignedRolesFromAPI];
    
    const otherSelectedRoles = usersInfo
      .filter((_, index) => index !== currentIndex && _.userId === userId)
      .map(user => user.role);
    
    const unavailableRoles = [...userAssignedRoles, ...otherSelectedRoles];
    
    return rolesOptions.filter(role => !unavailableRoles.includes(role.value));
  };
  
  const handleAdd = () => {
    setUsersInfo([...usersInfo, {
      id: usersInfo.length + 1,
      userId: "",
      role: "",
    }]);
  };

  const handleDelete = (index) => {
    const currentUsersInfo = [...usersInfo];
    const newUsersInfo = currentUsersInfo.filter((_, i) => i !== index);
    setUsersInfo(newUsersInfo);
  };

  const handleCancel = () => {
    setErrorMessage("");
    setUsersInfo([
      {
        id: 1,
        userId: "",
        role: "",
      }
    ])
    handleClose();
    setForceUpdate(!forceUpdate);
  };

  const handleSelect = (id, value, index) => {
    const currentUsersInfo = [...usersInfo];
    currentUsersInfo[index][id] = value;
    
    if (id === "userId") {
      currentUsersInfo[index].role = "";
    }
    
    setUsersInfo(currentUsersInfo);
  };

  const handleAddUsers = () => {
    const usersList = usersInfo.map((user) => ({
      userId: user.userId,
      roleId: user.role,
    }));

    const newUsers = usersList.filter(newUser => 
      !userRolesProject.some(existingUser => 
        existingUser.userId === newUser.userId && 
        existingUser.roleId === newUser.roleId
      )
    );

    setUserRolesProject([...userRolesProject, ...newUsers]);
    handleClose();
  };

  const handleAssignUser = () => {
    const usersList = usersInfo.map((user) => ({
      projectId: projectId,
      userId: user.userId,
      roleId: user.role,
    }));

    assignUser.reset();
    assignUser.mutate(usersList, {
      onSuccess: () => {
        setErrorMessage("");
        refetch();
        handleCancel();
      },
      onError: (err) => {
        setErrorMessage(err.response.data.Errors[0]);
      }
    });
  };

  return (
    <Modal
      open={openCreate}
      onCancel={handleCancel}
      align={"left"}
      padding={"41px 39px"}
      width={"776px"}
    >
      <Modal.Header 
        padding="16px 0 32px 0" 
        title={"Add New User to Project"} 
        align={"flex-start"} 
        subtitle={"Assign team members to give them access and start collaborating. To assign multiple roles, add the user separately for each role."} 
      />
      <Modal.Body
        margin="0 0 24px 0"
        maxHeight={400}
        style={{ paddingRight: "24px" }}
      >
        <Div direction="column" gap="24px">
          {usersInfo.map((user, index) => (
            <Div key={user.id}>
              <Row gutter={[13, 16]} style={{ width: "100%" }}>
                <Col md={10}>
                  <Select 
                    placeholder="Select user" 
                    onChange={(value) => handleSelect("userId", value, index)} 
                    value={user.userId || undefined}
                    options={activeUsersOptions}
                    autoComplete="off"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    notFoundContent="Data not found"
                    showSearch
                  />
                </Col>
                <Col md={10}>
                  <Select 
                    placeholder="Select role/roles" 
                    onChange={(value) => handleSelect("role", value, index)} 
                    value={user.role || undefined}
                    options={getFilteredRolesForUser(user.userId, index)}      autoComplete="off"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    notFoundContent="Data not found"
                    showSearch
                  />
                </Col>
                <Col md={4}>
                  {index !== 0 && (
                    <Link width="min-content" onClick={() => handleDelete(index)}>
                      <Div align="center" gap="4px" m="0 0 0 16px">
                        <img alt="delete" src={deleteIcon} />
                        Delete
                      </Div>
                    </Link>
                  )}
                </Col>
              </Row>
            </Div>
          ))}
        </Div>
        <Link width="min-content" onClick={handleAdd}>
          <Div align="center" gap="7px">
            <img alt="add" src={addIcon} />
            Add User
          </Div>
        </Link>
      </Modal.Body>
      <Modal.Footer style={{ flexDirection: "column" }}>
        {errorMessage !== "" && (
          <Div m="0 0 20px 0" justify="flex-end">
            <Alert closable message={`${errorMessage}`} type="error" />
          </Div>
        )}
        <Div gap="32px" justify="center">
          <Button
            onClick={handleCancel}
            border={theme.colors.dark}
            color={theme.colors.dark}
          >
            Cancel
          </Button>
          <Button
            onClick={source === "usersByProject" ? handleAssignUser : handleAddUsers}
            background={theme.colors.salmon}
            loading={assignUser.isLoading}
          >
            Add users
          </Button>
        </Div>
      </Modal.Footer>
    </Modal>
  )
};